import type {
	Conversation,
	ConversationResponse,
	Gift,
	Message,
	MessengerPagination,
	MessengerPrices,
	UnreadCountResponse,
} from "@shared/types";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api-client";
import { hasUserCookie } from "@/lib/auth-cookie";
import { queryKeys } from "@/lib/query-keys";

/** Raw backend conversation profile with online as 0|1 */
export interface RawConversationProfile {
	id: number;
	name: string;
	age?: number;
	gender?: "m" | "f";
	badge?: string | null;
	friend?: boolean;
	favorite?: boolean;
	online: number | boolean;
	image: {
		thumb: string;
		isBlurred: boolean;
		isErotic: number;
		isFriend: number;
	} | null;
}

/**
 * Backend liefert bei gelöschtem Profil statt eines Objekts die nackte profile_id (Integer).
 * Diese Helper-Funktion glättet das auf null, damit der Render-Code sauber `?? null` greifen kann.
 */
export function mapProfile(
	raw: RawConversationProfile | number | null | undefined,
): import("@shared/types").ConversationProfile | null {
	if (!raw || typeof raw !== "object") return null;
	return {
		...raw,
		online: raw.online === 1 || raw.online === true,
	};
}

interface RawConversation extends Omit<Conversation, "profile"> {
	profile: RawConversationProfile | number | null;
}

interface RawConversationsResponse {
	conversations: RawConversation[];
	pagination?: MessengerPagination;
}

function mapConversation(raw: RawConversation): Conversation {
	return {
		...raw,
		profile: mapProfile(raw.profile),
	};
}

interface ConversationsPage {
	conversations: Conversation[];
	pagination?: MessengerPagination;
}

interface ConversationsInfiniteData {
	pages: ConversationsPage[];
	pageParams: unknown[];
}

export function useConversations() {
	const query = useInfiniteQuery({
		queryKey: queryKeys.messenger.conversations(),
		queryFn: async ({ signal, pageParam }): Promise<ConversationsPage> => {
			const response = await api.get<RawConversationsResponse>(
				`/api/v3/messenger/conversations?page=${pageParam}`,
				signal,
			);
			return {
				conversations: response.conversations.map(mapConversation),
				pagination: response.pagination,
			};
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.pagination?.has_more ? lastPage.pagination.current_page + 1 : undefined,
		enabled: hasUserCookie(),
		staleTime: 1000 * 60,
	});

	const conversations = useMemo<Conversation[]>(() => {
		const seen = new Set<number>();
		return (
			query.data?.pages
				.flatMap((p) => p.conversations)
				.filter((c) => {
					if (seen.has(c.id)) return false;
					seen.add(c.id);
					return true;
				}) ?? []
		);
	}, [query.data]);

	return {
		conversations,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		hasNextPage: query.hasNextPage,
		isFetchingNextPage: query.isFetchingNextPage,
		fetchNextPage: query.fetchNextPage,
	};
}

export function useConversationInfo(id: string | null) {
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: queryKeys.messenger.conversationInfo(id ?? ""),
		queryFn: async ({ signal }): Promise<Conversation> => {
			const response = await api.get<RawConversation>(
				`/api/v3/messenger/conversation/${id}/info`,
				signal,
			);
			return mapConversation(response);
		},
		enabled: hasUserCookie() && id != null,
		staleTime: 1000 * 30,
		// Seed aus dem Listen-Cache: wenn die Conversation bereits in einer geladenen Page lebt,
		// kein Extra-Roundtrip beim ersten Render.
		initialData: () => {
			if (!id) return undefined;
			const numericId = Number.parseInt(id, 10);
			if (Number.isNaN(numericId)) return undefined;
			const cached = queryClient.getQueryData<ConversationsInfiniteData>(
				queryKeys.messenger.conversations(),
			);
			if (!cached) return undefined;
			for (const page of cached.pages) {
				const hit = page.conversations.find((c) => c.id === numericId);
				if (hit) return hit;
			}
			return undefined;
		},
		initialDataUpdatedAt: () =>
			queryClient.getQueryState(queryKeys.messenger.conversations())?.dataUpdatedAt,
	});
}

/** Raw message format from the backend API */
interface RawMessage {
	direction: string;
	message: string | null;
	created_at: string;
	type: string;
	image: { url: string; thumb: string; width: number; height: number } | null;
}

/** Generate a stable numeric hash from message content for deduplication */
function messageHash(raw: RawMessage): number {
	const key = `${raw.created_at}|${raw.direction}|${raw.type}|${raw.message ?? ""}`;
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
}

/** Map raw backend message to our Message interface */
function mapMessage(raw: RawMessage): Message {
	const isGift = (raw.type ?? "text") === "gift";
	return {
		id: messageHash(raw),
		type: (raw.type ?? "text") as Message["type"],
		text: raw.message,
		image: !isGift ? (raw.image?.url ?? raw.image?.thumb ?? null) : null,
		gift: isGift && raw.image ? { id: 0, name: "Geschenk", img: raw.image.url } : null,
		from_me: raw.direction?.toUpperCase() === "IN",
		created_at: raw.created_at?.replace(" ", "T") ?? new Date().toISOString(),
	};
}

/** Paginated response from Laravel backend */
interface PaginatedResponse<T> {
	data: T[];
	current_page: number;
	last_page: number;
}

export function useConversation(id: string | null, page = 0) {
	return useQuery({
		queryKey: [...queryKeys.messenger.conversation(id ?? ""), page],
		queryFn: async ({ signal }) => {
			const response = await api.get<PaginatedResponse<RawMessage> | RawMessage[]>(
				`/api/v3/messenger/conversation/${id}?page=${page}`,
				signal,
			);

			let rawMessages: RawMessage[];
			let currentPage = 1;
			let lastPage = 1;

			if (Array.isArray(response)) {
				rawMessages = response;
			} else if (response && typeof response === "object" && "data" in response) {
				rawMessages = Array.isArray(response.data) ? response.data : [];
				currentPage = response.current_page;
				lastPage = response.last_page;
			} else {
				rawMessages = [];
			}

			// Backend liefert newest-first -> reverse für chronologische Reihenfolge
			const messages = rawMessages.map(mapMessage).reverse();

			return {
				data: messages,
				current_page: currentPage,
				last_page: lastPage,
			} satisfies ConversationResponse;
		},
		enabled: hasUserCookie() && id != null,
		staleTime: 1000 * 30,
	});
}

export function useUnreadCount() {
	return useQuery({
		queryKey: queryKeys.messenger.unreadCount(),
		queryFn: async ({ signal }) => {
			const response = await api.get<UnreadCountResponse>("/api/v3/messenger/unread-count", signal);
			return response.count;
		},
		enabled: hasUserCookie(),
		staleTime: 1000 * 30,
		refetchInterval: 1000 * 60,
	});
}

export function useGifts() {
	return useQuery({
		queryKey: queryKeys.messenger.gifts(),
		queryFn: async ({ signal }) => {
			// Backend returns flat array
			const response = await api.get<Gift[]>("/api/v3/messenger/gifts", signal);
			return response;
		},
		enabled: hasUserCookie(),
		staleTime: Number.POSITIVE_INFINITY,
	});
}

export function usePrices() {
	return useQuery({
		queryKey: queryKeys.messenger.prices(),
		queryFn: async ({ signal }) => {
			// Backend returns MessengerPrices directly (no wrapper)
			const response = await api.get<MessengerPrices>("/api/v3/messenger/prices", signal);
			return response;
		},
		enabled: hasUserCookie(),
		staleTime: Number.POSITIVE_INFINITY,
	});
}
